import React, { useEffect, useRef, useState } from 'react';
import {
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    IconButton,
    Alert,
    CircularProgress,
} from '@mui/material';
import moment from 'moment';
import 'moment/locale/es';
import { MainCard } from './MainCard';
import SignatureCanvas from 'react-signature-canvas';
import { grey } from '@mui/material/colors';
import { Autorenew as AutorenewIcon } from '@mui/icons-material';
import { convertToFile } from '../utils';
import { useParams, useNavigate } from 'react-router-dom';
import { PDFFile } from './PDFFile';
import { PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { uploadFileToAWS } from '../utils/uploadFileToAWS';
import { handleValidateAuthorization, sendScoreToWarbox } from './utils';

moment.locale('es');

const styles = {
    head: {
        fontWeight: 700,
        width: '28%',
    },
    text: {
        textTransform: 'capitalize',
    },
    btn: {
        color: '#fff',
    },
};

export const ReviewInfo = ({ initialValues, setFormState }) => {
    const {
        primerNombre,
        segundoNombre,
        apellidoPaterno,
        apellidoMaterno,
        email,
        fechaNacimiento,
        direccion1,
        coloniaPoblacion,
        delegacionMunicipio,
        estado,
        cp,
        RFC,
        ciudad,
    } = initialValues;

    const [errors, setErrors] = useState({
        sign: null,
        rfc: null,
    });

    const params = useParams();

    const { token } = params;

    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);

    const [currentRFC, setCurrentRFC] = useState('');

    const [response, setResponse] = useState(null);

    const ref = useRef(null);

    const handleBack = () => setFormState({ view: 'form', initialValues });

    const handleSign = () => {
        const sign = ref.current.getTrimmedCanvas().toDataURL('image/png');
        setSign(sign);
    };

    const [sign, setSign] = useState(null);

    const handleUploadSignature = async () => {
        const signFile = convertToFile(sign, initialValues.primerNombre);

        return await uploadFileToAWS(
            signFile,
            '',
            `Firma_${initialValues.primerNombre}_${initialValues.apellidoPaterno}_${initialValues.apellidoMaterno}.png`
        );
    };

    const handleUploadPDF = async () => {
        const pdfFile = await pdf(
            <PDFFile values={{ ...initialValues, RFC: currentRFC, sign }} />
        ).toBlob();

        const folder = 'Autorizaciones_BC_Score';

        const date = `${moment().format('MMMM[_]YYYY')}`;

        const week = moment().format('W');

        const fileName = `Solicitud_BC_Score_${initialValues.primerNombre}_${initialValues.apellidoPaterno}_${initialValues.apellidoMaterno}.pdf`;

        return await uploadFileToAWS(
            pdfFile,
            `${folder}/${date}/${week}`,
            fileName
        );
    };

    const clearResponse = () => setTimeout(() => setResponse(null), 5000);

    const handleSendData = async () => {
        setLoading(true);

        const { validations, response } = handleValidateAuthorization(
            sign,
            currentRFC
        );

        if (!response) {
            setErrors(validations);
            return false;
        }

        const signature = await handleUploadSignature();

        await handleUploadPDF();

        await sendScoreToWarbox(
            { ...initialValues, signature, currentRFC },
            token,
            (response) => {
                const { success, data, error } = response;

                if (success) {
                    setResponse(data);
                    setLoading(false);
                    navigate(`/success`);
                } else {
                    setResponse(error);
                    clearResponse();
                    setLoading(false);
                }
            }
        );
    };

    useEffect(() => {
        setCurrentRFC(RFC);
    }, [RFC]);

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Typography textAlign="center" variant="h6">
                Confirma tu Información
            </Typography>
            <MainCard
                data-testid="confirmation-modal"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell style={styles.head}>Nombre</TableCell>
                            <TableCell
                                style={styles.text}
                                data-testid="name-row"
                            >
                                {`${primerNombre} ${segundoNombre} ${apellidoPaterno} ${apellidoMaterno}`}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell style={styles.head}>Correo</TableCell>
                            <TableCell
                                style={styles.text}
                                data-testid="email-row"
                            >
                                {email}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell style={styles.head}>
                                Fecha de Nacimiento
                            </TableCell>
                            <TableCell
                                style={styles.text}
                                data-testid="birthdate-row"
                            >
                                {moment(fechaNacimiento).format(
                                    'DD [de] MMMM [del] YYYY'
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell style={styles.head}>Domicilio</TableCell>
                            <TableCell
                                style={styles.text}
                                data-testid="address-row"
                            >
                                {`${direccion1} ${coloniaPoblacion}, ${delegacionMunicipio}, ${ciudad}, ${estado}, CP: ${cp}`}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell style={styles.head}>RFC</TableCell>
                            <TableCell style={styles.text}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    error={errors.rfc}
                                    value={currentRFC}
                                    inputProps={{
                                        'data-testid': 'RFC-field',
                                    }}
                                    onChange={(e) =>
                                        setCurrentRFC(e.target.value)
                                    }
                                    InputProps={{
                                        sx: {
                                            height: 25,
                                            fontSize: 13,
                                            padding: 0,
                                            margin: 0,
                                        },
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    color={
                                        errors.rfc ? 'error' : 'textSecondary'
                                    }
                                    sx={{ fontSize: 10 }}
                                >
                                    {errors.rfc ||
                                        'En caso que tu RFC no coincida revisa tu información o modifica tu RFC aquí'}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                <Box
                    my={2}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={1}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <SignatureCanvas
                            canvasProps={{
                                width: 400,
                                height: 100,
                            }}
                            backgroundColor={'#f5f5f5'}
                            onEnd={handleSign}
                            ref={ref}
                        />
                        {errors.sign && (
                            <Typography
                                variant="caption"
                                color={errors.sign ? 'error' : 'textSecondary'}
                                sx={{ fontSize: 10 }}
                            >
                                {errors.sign}
                            </Typography>
                        )}
                        <IconButton
                            onClick={() => ref?.current?.clear()}
                            sx={{ position: 'absolute', top: 0, right: 0 }}
                        >
                            <AutorenewIcon
                                sx={{ width: 20, color: grey[900] }}
                            />
                        </IconButton>
                    </Box>

                    <Typography variant="body1">
                        Firma de Autorización
                    </Typography>
                </Box>

                <Box>
                    <p
                        variant="caption"
                        style={{
                            marginTop: 2,
                            fontSize: 8,
                            lineHeight: 1.3,
                            textAlign: 'justify',
                            textJustify: 'inner-word',
                            color: grey[600],
                        }}
                    >
                        Por este conducto autorizo expresamente a CAROKOU MEXICO
                        SAPI DE CV, para que por conducto de sus funcionarios
                        facultados lleve a cabo Investigaciones, sobre mi
                        comportamiento crediticio o el de la Empresa que
                        represento, declaro que conozco la naturaleza y alcance
                        de la información que se solicitará, del uso que CAROKOU
                        MEXICO SAPI DE CV, hará de tal información y de que ésta
                        podrá realizar consultas periódicas sobre mi historial o
                        el de la empresa que represento, consintiendo que esta
                        autorización se encuentre vigente por un período de 3
                        años contados a partir de su expedición y en todo caso
                        durante el tiempo que se mantenga la relación juridica.
                    </p>
                </Box>
                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        gap={1}
                        alignItems="center"
                    >
                        <CircularProgress />
                        <Typography variant="body1">
                            Procesando su solicitud
                        </Typography>
                    </Box>
                ) : (
                    <Box display="flex" justifyContent="space-between" mt={2}>
                        <Button
                            onClick={handleBack}
                            variant="contained"
                            color="secondary"
                            sx={{
                                color: '#FFF',
                                height: 50,
                                minWidth: 150,
                            }}
                        >
                            Atrás
                        </Button>
                        <PDFDownloadLink
                            document={
                                <PDFFile
                                    values={{
                                        ...initialValues,
                                        RFC: currentRFC,
                                        sign,
                                    }}
                                />
                            }
                            fileName={`Autorización_${initialValues.primerNombre}_${initialValues.apellidoPaterno}_${initialValues.apellidoMaterno}.pdf`}
                        >
                            <Button
                                data-testid="submit-button"
                                variant="contained"
                                color="secondary"
                                disabled={!sign}
                                onClick={handleSendData}
                                sx={{
                                    color: '#FFF',
                                    height: 50,
                                    minWidth: 150,
                                }}
                            >
                                Autorizo la consulta
                            </Button>
                        </PDFDownloadLink>
                    </Box>
                )}
            </MainCard>
            {response?.error && (
                <Alert severity="error">{response.error}</Alert>
            )}
        </Box>
    );
};
